import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";
import { Plus, X, Loader2, FileSignature, Copy } from "lucide-react";
import EmailStatusBadge from "../components/EmailStatusBadge";

function fmtDate(iso) { if (!iso) return ""; return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }

export default function EsignPage() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const reload = async () => {
    setLoading(true);
    const [r, c] = await Promise.all([axios.get(`${API_BASE}/esign`), axios.get(`${API_BASE}/clients`)]);
    setRows(r.data); setClients(c.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "—";

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="esign-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Signatures</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>eSign requests</h1>
          <p className="text-[var(--ink-2)] mt-2">Send a document for in-house electronic signature with audit trail.</p>
        </div>
        <button onClick={() => setShow(true)} className="btn-primary" data-testid="esign-create-btn">
          <Plus size={14} /> New request
        </button>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="esign-empty">
          <FileSignature size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No signing requests yet</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>Title</th><th>Client</th><th>Signer</th><th>Status</th><th>Email</th><th>Signed</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.esign_id} data-testid={`esign-row-${r.esign_id}`}>
                  <td className="font-medium">{r.title}</td>
                  <td>{clientName(r.client_id)}</td>
                  <td className="font-mono text-[12px]">{r.signer_email}</td>
                  <td>
                    <span className="badge" style={{
                      background: r.status === "signed" ? "var(--moss)" : "var(--accent)", color: "var(--paper)"
                    }}>{r.status}</span>
                  </td>
                  <td><EmailStatusBadge status={r.last_email_status} testid={`esign-email-status-${r.esign_id}`} /></td>
                  <td className="text-[12px] text-[var(--ink-3)] font-mono">{r.signed_at ? fmtDate(r.signed_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {show && <EsignModal clients={clients} onClose={() => setShow(false)} onCreated={() => { setShow(false); reload(); }} />}
    </div>
  );
}

function EsignModal({ clients, onClose, onCreated }) {
  const [form, setForm] = useState({ client_id: clients[0]?.client_id || "", title: "", body_md: "", signer_email: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API_BASE}/esign`, form);
      setResult(r.data);
      toast.success("eSign request created");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    const link = result?.signer_link?.link;
    if (!link) return;
    const full = link.startsWith("http") ? link : window.location.origin + link;
    try { await navigator.clipboard.writeText(full); toast.success("Link copied"); }
    catch { toast.error("Copy failed: " + full); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,24,20,0.45)" }} data-testid="esign-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">New eSign</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Request a signature</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="esign-modal-close"><X size={20} /></button>
        </div>

        {result ? (
          <div data-testid="esign-result">
            <div className="text-[14px] mb-4">eSign request <b>{result.esign.title}</b> ready.</div>
            <div className="card p-4 mb-4" style={{ background: "var(--subtle)" }}>
              <div className="overline mb-1">Signer link</div>
              <div className="text-[12px] font-mono break-all">
                {result.signer_link?.link
                  ? (result.signer_link.link.startsWith("http") ? result.signer_link.link : window.location.origin + result.signer_link.link)
                  : "Signer must be a client portal user — invite them on the Clients page first."}
              </div>
              {result.signer_link?.email_result?.ok === false && (
                <div className="text-[12px] text-[var(--accent)] mt-2">
                  Email could not be delivered — copy the link to share manually.
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={copyLink} className="btn-secondary flex-1 justify-center" data-testid="esign-copy-link"><Copy size={12} /> Copy link</button>
              <button onClick={onCreated} className="btn-primary flex-1 justify-center" data-testid="esign-done">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="esign-form">
            <div>
              <label className="overline block mb-1.5">Client</label>
              <select value={form.client_id} onChange={(e) => upd("client_id")(e.target.value)} data-testid="esign-client"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]">
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1.5">Title</label>
              <input value={form.title} required onChange={(e) => upd("title")(e.target.value)} data-testid="esign-title"
                placeholder="e.g. Engagement letter — FY26"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Document body (markdown)</label>
              <textarea value={form.body_md} required minLength={10} onChange={(e) => upd("body_md")(e.target.value)} data-testid="esign-body"
                rows={8} placeholder="Paste your engagement letter or other document content here…"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] font-mono" />
            </div>
            <div>
              <label className="overline block mb-1.5">Signer email</label>
              <input type="email" value={form.signer_email} required onChange={(e) => upd("signer_email")(e.target.value)} data-testid="esign-signer-email"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
            </div>

            {err && <div className="text-[13px] text-[var(--accent)]" data-testid="esign-error">{err}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="esign-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}{busy ? "Sending…" : "Create & send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
